import {
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  QueryStats as DataConsistencyIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  GridLegacy as Grid,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AdminFilterPanel, PageShell } from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import {
  AnalyticsServiceV2,
  type DailyFootTrafficPoint,
  type DailySalesDataPoint,
} from "@/services/features/AnalyticsServiceV2";
import { type Customer, type Store, StoreService } from "@/services/features/StoreService";
import { formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel } from "@/utils/store";

const TIME_RANGE_OPTIONS = [
  {
    value: "last14Days",
    labelKey: "dataConsistency.timeRanges.last14Days",
    days: 14,
  },
  {
    value: "last30Days",
    labelKey: "dataConsistency.timeRanges.last30Days",
    days: 30,
  },
  {
    value: "last90Days",
    labelKey: "dataConsistency.timeRanges.last90Days",
    days: 90,
  },
  {
    value: "last180Days",
    labelKey: "dataConsistency.timeRanges.last180Days",
    days: 180,
  },
  {
    value: "last365Days",
    labelKey: "dataConsistency.timeRanges.last365Days",
    days: 365,
  },
] as const;

type TimeRangeOption = (typeof TIME_RANGE_OPTIONS)[number]["value"];

type ChartRow = {
  date: string;
  [key: string]: string | number;
};

const SERIES_COLORS = [
  "#0f766e",
  "#2563eb",
  "#dc2626",
  "#7c3aed",
  "#ea580c",
  "#059669",
  "#db2777",
  "#4f46e5",
];
const CHART_HEIGHT = { xs: 420, md: 480, xl: 540 };

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDateRange(option: TimeRangeOption): {
  startDate: string;
  endDate: string;
} {
  const days =
    TIME_RANGE_OPTIONS.find((timeRangeOption) => timeRangeOption.value === option)?.days ?? 14;
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { startDate: formatDate(start), endDate: formatDate(end) };
}

function listDates(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const cursor = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);

  while (cursor <= end) {
    dates.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function formatNumber(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function buildChartData<T extends { storeId: number; localCreatedAtDate: string }>(
  points: T[],
  selectedStores: Store[],
  startDate: string,
  endDate: string,
  getValue: (point: T) => number,
): ChartRow[] {
  const dates = listDates(startDate, endDate);
  const rows = new Map<string, ChartRow>();

  for (const date of dates) {
    const row: ChartRow = { date };
    for (const store of selectedStores) {
      row[`store_${store.id}`] = 0;
    }
    rows.set(date, row);
  }

  for (const point of points) {
    const row = rows.get(point.localCreatedAtDate);
    if (!row) {
      continue;
    }
    row[`store_${point.storeId}`] = getValue(point);
  }

  return dates.map((date) => rows.get(date) || { date });
}

type DataConsistencyChartCardProps = {
  title: string;
  data: ChartRow[];
  selectedStores: Store[];
  valueFormatter: (value: number) => string;
  responseStoreIds: Set<number>;
};

type ChartLegendPayloadItem = {
  color?: string;
  dataKey?: string | number;
  value?: string;
};

const DataConsistencyChartCard: React.FC<DataConsistencyChartCardProps> = ({
  title,
  data,
  selectedStores,
  valueFormatter,
  responseStoreIds,
}) => {
  const { t } = useTranslation();
  const [hoveredStoreId, setHoveredStoreId] = React.useState<number | null>(null);
  const missingStores = selectedStores.filter((store) => !responseStoreIds.has(store.id));
  const responseStoreCount = responseStoreIds.size;
  const missingStoreCount = Math.max(selectedStores.length - responseStoreCount, 0);

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            color={missingStoreCount > 0 ? "warning.main" : "text.secondary"}
          >
            {t("dataConsistency.text.storesInResponse", {
              responseStoreCount,
              selectedStoreCount: selectedStores.length,
            })}
          </Typography>
        </Stack>

        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="stretch">
          <Box sx={{ flex: 1, minWidth: 0, height: CHART_HEIGHT }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis
                  tickFormatter={(value: number) => valueFormatter(value)}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(value, _name, item) => [
                    valueFormatter(Number(value || 0)),
                    item?.name || "",
                  ]}
                />
                <Legend
                  content={({ payload }) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 1.5,
                        pt: 1,
                      }}
                    >
                      {(payload as ChartLegendPayloadItem[] | undefined)?.map((entry) => {
                        const storeId = Number(String(entry.dataKey || "").replace("store_", ""));
                        const isHighlighted = hoveredStoreId === null || hoveredStoreId === storeId;

                        return (
                          <Box
                            key={String(entry.dataKey)}
                            onMouseEnter={() => setHoveredStoreId(storeId)}
                            onMouseLeave={() => setHoveredStoreId(null)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              cursor: "pointer",
                              opacity: isHighlighted ? 1 : 0.35,
                              transition: "opacity 150ms ease",
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: entry.color || "text.secondary",
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: hoveredStoreId === storeId ? 700 : 500,
                              }}
                            >
                              {entry.value || ""}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                />
                {selectedStores.map((store, index) => (
                  <Line
                    key={store.id}
                    type="monotone"
                    dataKey={`store_${store.id}`}
                    name={store.name}
                    stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                    strokeOpacity={hoveredStoreId === null || hoveredStoreId === store.id ? 1 : 0.2}
                    strokeWidth={hoveredStoreId === store.id ? 4 : 2.5}
                    dot={false}
                    activeDot={{ r: hoveredStoreId === store.id ? 6 : 5 }}
                    onMouseEnter={() => setHoveredStoreId(store.id)}
                    onMouseLeave={() => setHoveredStoreId(null)}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          <Box
            sx={{
              width: { xs: "100%", lg: 300 },
              flexShrink: 0,
              maxHeight: { xs: "none", lg: 540 },
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              bgcolor: "grey.50",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t("dataConsistency.text.storesWithNoData")}
            </Typography>

            {missingStores.length > 0 ? (
              <Stack spacing={0.75}>
                {missingStores.map((store) => (
                  <Typography key={store.id} variant="body2" color="text.secondary">
                    {store.name}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("dataConsistency.empty.allSelectedStoresReturnedData")}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const DataConsistency: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const { permissions, user } = useAuth();
  const isCraveUser = permissions.isCraveUser();
  const storeService = React.useMemo(() => StoreService.getInstance(), []);
  const analyticsService = React.useMemo(() => AnalyticsServiceV2.getInstance(), []);

  const [customers, setCustomers] = React.useState<Customer[]>([]);
  const [stores, setStores] = React.useState<Store[]>([]);
  const [selectedCustomerIds, setSelectedCustomerIds] = React.useState<number[]>([]);
  const [selectedStores, setSelectedStores] = React.useState<Store[]>([]);
  const [timeRange, setTimeRange] = React.useState<TimeRangeOption>("last14Days");
  const [footTraffic, setFootTraffic] = React.useState<DailyFootTrafficPoint[]>([]);
  const [salesData, setSalesData] = React.useState<DailySalesDataPoint[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = React.useState(true);
  const [isLoadingStores, setIsLoadingStores] = React.useState(false);
  const [isLoadingCharts, setIsLoadingCharts] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function loadFilters() {
      try {
        setIsLoadingCustomers(true);
        setIsLoadingStores(false);
        setError(null);

        const customersResponse = await storeService.getCustomers();
        if (cancelled) {
          return;
        }

        setCustomers(customersResponse.data || []);
        setIsLoadingCustomers(false);
        setIsLoadingStores(true);

        const storesResponse = await storeService.getStores();

        if (cancelled) {
          return;
        }

        const nextStores = storesResponse.data || [];

        setStores(nextStores);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || t("dataConsistency.errors.failedLoadFilters"));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCustomers(false);
          setIsLoadingStores(false);
        }
      }
    }

    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [isCraveUser, storeService, t]);

  const visibleStores = React.useMemo(() => {
    if (!selectedCustomerIds.length) {
      return stores;
    }
    const customerIdSet = new Set(selectedCustomerIds);
    return stores.filter((store) => customerIdSet.has(store.customerId));
  }, [selectedCustomerIds, stores]);

  React.useEffect(() => {
    setSelectedStores((current) => {
      const visibleIds = new Set(visibleStores.map((store) => store.id));
      return current.filter((store) => visibleIds.has(store.id));
    });
  }, [visibleStores]);

  const { startDate, endDate } = React.useMemo(() => getDateRange(timeRange), [timeRange]);
  const canQuery = selectedStores.length > 0;

  React.useEffect(() => {
    let cancelled = false;

    async function loadCharts() {
      if (!canQuery) {
        setFootTraffic([]);
        setSalesData([]);
        return;
      }

      try {
        setIsLoadingCharts(true);
        setError(null);

        const storeIds = selectedStores.map((store) => store.id);
        const footTrafficResponse = await analyticsService.getDailyFootTraffic({
          storeIds,
          startDate,
          endDate,
        });
        const salesDataResponse = await analyticsService.getDailySalesData({
          storeIds,
          startDate,
          endDate,
        });

        if (cancelled) {
          return;
        }

        setFootTraffic(footTrafficResponse.data || []);
        setSalesData(salesDataResponse.data || []);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || t("dataConsistency.errors.failedLoadChartData"));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingCharts(false);
        }
      }
    }

    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [analyticsService, canQuery, endDate, selectedStores, startDate, t]);

  const footTrafficChartData = React.useMemo(
    () =>
      buildChartData(footTraffic, selectedStores, startDate, endDate, (point) => point.count || 0),
    [endDate, footTraffic, selectedStores, startDate],
  );

  const salesChartData = React.useMemo(
    () =>
      buildChartData(
        salesData,
        selectedStores,
        startDate,
        endDate,
        (point) => point.totalPrice || 0,
      ),
    [endDate, salesData, selectedStores, startDate],
  );

  const hasMixedCurrencies = React.useMemo(() => {
    const currencies = new Set(selectedStores.map((store) => store.currencyCode).filter(Boolean));
    return currencies.size > 1;
  }, [selectedStores]);

  const footTrafficResponseStoreIds = React.useMemo(
    () => new Set(footTraffic.map((point) => point.storeId)),
    [footTraffic],
  );

  const salesResponseStoreIds = React.useMemo(
    () => new Set(salesData.map((point) => point.storeId)),
    [salesData],
  );

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("dataConsistency.title")}
      icon={<DataConsistencyIcon />}
      titleAccessory={
        <LoadingRing loading={isLoadingCustomers || isLoadingStores || isLoadingCharts} />
      }
    >
      <Box sx={{ p: 0 }}>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          <AdminFilterPanel
            sx={{ borderRadius: 3 }}
            hint={
              !canQuery && !isLoadingCustomers && !isLoadingStores ? (
                <Alert severity="info">
                  {isCraveUser
                    ? t("dataConsistency.empty.selectOneOrMoreStores")
                    : t("dataConsistency.empty.selectAtLeastOneStore")}
                </Alert>
              ) : null
            }
          >
            <Grid container spacing={2} alignItems="center">
              {isCraveUser && (
                <Grid item xs={12} lg>
                  <MuiAutocompleteMultiSelect
                    options={customers}
                    value={customers.filter((customer) =>
                      selectedCustomerIds.includes(customer.id),
                    )}
                    onChange={(next) => {
                      const nextCustomerIds = next.map((customer) => customer.id);
                      setSelectedCustomerIds(nextCustomerIds);

                      const customerIdSet = new Set(nextCustomerIds);
                      const nextStores =
                        nextCustomerIds.length === 0
                          ? []
                          : stores.filter((store) => customerIdSet.has(store.customerId));
                      setSelectedStores(nextStores);
                    }}
                    label={t("dataConsistency.fields.customers")}
                    multiSelect
                    getOptionLabel={(customer) => customer.name}
                    getOptionKey={(customer) => customer.id}
                    emptyText={t("dataConsistency.empty.noCustomers")}
                    loading={isLoadingCustomers}
                    disabled={isLoadingCustomers}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              )}

              <Grid item xs={12} lg>
                <MuiAutocompleteMultiSelect
                  options={visibleStores}
                  value={selectedStores}
                  onChange={setSelectedStores}
                  label={t("dataConsistency.fields.stores")}
                  multiSelect
                  rowHeight="default"
                  getOptionLabel={(store) => formatStoreSelectorLabel(store, t, user?.role, "name")}
                  getOptionKey={(store) => store.id}
                  getOptionSecondary={(store) =>
                    `${store.customerName}${store.group ? ` • ${store.group}` : ""}`
                  }
                  emptyText={t("dataConsistency.empty.noStores")}
                  currentUserRole={user?.role}
                  loading={isLoadingStores}
                  disabled={isLoadingStores}
                  sx={{ width: "100%" }}
                />
              </Grid>

              <Grid item xs={12} lg>
                <MuiAutocompleteMultiSelect
                  sx={{ width: "100%" }}
                  label={t("dataConsistency.fields.timeRange")}
                  options={TIME_RANGE_OPTIONS}
                  value={TIME_RANGE_OPTIONS.filter((option) => option.value === timeRange)}
                  onChange={(next) => {
                    const nextRange = next[0];
                    if (nextRange) {
                      setTimeRange(nextRange.value);
                    }
                  }}
                  getOptionLabel={(option) => t(option.labelKey)}
                  getOptionKey={(option) => option.value}
                />
              </Grid>

              <Grid item xs={12} lg="auto" sx={{ alignSelf: "center" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  startIcon={<ClearIcon />}
                  disabled={
                    selectedCustomerIds.length === 0 &&
                    selectedStores.length === 0 &&
                    timeRange === "last14Days"
                  }
                  onClick={() => {
                    setSelectedCustomerIds([]);
                    setSelectedStores([]);
                    setTimeRange("last14Days");
                  }}
                  sx={{ width: { xs: "100%", lg: "auto" } }}
                >
                  {t("common.clearFilters")}
                </Button>
              </Grid>
            </Grid>
          </AdminFilterPanel>

          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Card sx={{ flex: 1, minWidth: 0, p: 2, display: "flex", alignItems: "center" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <DateRangeIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("dataConsistency.fields.timeRange")}
                  </Typography>
                  <Typography variant="h6">
                    {t("dataConsistency.text.range", { startDate, endDate })}
                  </Typography>
                </Box>
              </Stack>
            </Card>
            <Card sx={{ flex: 1, minWidth: 0, p: 2, display: "flex", alignItems: "center" }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <StoreIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {t("dataConsistency.fields.stores")}
                  </Typography>
                  <Typography variant="h4">
                    {formatInteger(selectedStores.length, locale)}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Stack>

          {canQuery && hasMixedCurrencies && (
            <Alert severity="info">{t("dataConsistency.text.multipleCurrencies")}</Alert>
          )}

          {canQuery && (
            <DataConsistencyChartCard
              title={t("dataConsistency.charts.footTrafficByDay")}
              data={footTrafficChartData}
              selectedStores={selectedStores}
              valueFormatter={formatNumber}
              responseStoreIds={footTrafficResponseStoreIds}
            />
          )}
          {canQuery && (
            <DataConsistencyChartCard
              title={t("dataConsistency.charts.salesByDay")}
              data={salesChartData}
              selectedStores={selectedStores}
              valueFormatter={formatNumber}
              responseStoreIds={salesResponseStoreIds}
            />
          )}
        </Stack>
      </Box>
    </PageShell>
  );
};

export default DataConsistency;
